import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getBusinesses } from '../../services/businessService';
import {
  buildBusinessPortalUrl,
  findLaunchableBusinessForUser,
  getPortalLabel,
} from '../../utils/portalRegistry';

function StaffDashboardPage({ user, token, onLogout }) {
  const location = useLocation();
  const [businesses, setBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [businessError, setBusinessError] = useState('');
  const assignedPortalBusiness = useMemo(
    () => findLaunchableBusinessForUser(businesses, user),
    [businesses, user]
  );
  const portalLabel = getPortalLabel(assignedPortalBusiness);
  const shouldStayInPortalSelector = new URLSearchParams(location.search).get('stay') === '1';
  const portalRole =
    user?.role_name === 'admin-custom'
      ? 'admin-custom'
      : user?.role_name === 'accountant'
        ? 'accountant'
        : 'staff';
  const seaweedAccessTabs = user?.seaweed_access_tabs;
  const accessLabel =
    portalRole === 'admin-custom'
      ? 'Custom admin access'
      : portalRole === 'accountant'
        ? 'Accountant access'
        : 'Staff access';

  useEffect(() => {
    let ignore = false;

    async function loadBusinesses() {
      setLoadingBusinesses(true);
      setBusinessError('');

      try {
        const response = await getBusinesses(token);

        if (!ignore) {
          setBusinesses(response.data || []);
        }
      } catch (error) {
        if (!ignore) {
          setBusinessError(error.message || 'Unable to load assigned businesses.');
        }
      } finally {
        if (!ignore) {
          setLoadingBusinesses(false);
        }
      }
    }

    loadBusinesses();

    return () => {
      ignore = true;
    };
  }, [token]);

  useEffect(() => {
    if (loadingBusinesses || shouldStayInPortalSelector || !assignedPortalBusiness) {
      return;
    }

    window.location.replace(
      buildBusinessPortalUrl({
        business: assignedPortalBusiness,
        portalRole,
        token,
        hideBackAction: portalRole === 'admin-custom',
        seaweedAccessTabs,
      })
    );
  }, [
    assignedPortalBusiness,
    loadingBusinesses,
    portalRole,
    seaweedAccessTabs,
    shouldStayInPortalSelector,
    token,
  ]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#fcfbf9] px-5 text-[#111111]">
      <div className="w-full max-w-md rounded-[26px] border border-[#ece6df] bg-white p-6 text-center shadow-[0_16px_40px_rgba(69,48,33,0.05)]">
        <p className="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-[#93857b]">
          {accessLabel}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-[-0.04em]">
          {loadingBusinesses ? 'Opening your workspace...' : `${portalLabel} ${accessLabel.toLowerCase()}`}
        </h1>
        <p className="mt-3 text-sm leading-6 text-[#8b7a6d]">
          {businessError ||
            (!loadingBusinesses && !assignedPortalBusiness
              ? 'No open business portal is assigned to this account yet.'
              : `Redirecting ${user?.username || 'staff'} to the assigned workspace.`)}
        </p>

        {!loadingBusinesses && !assignedPortalBusiness ? (
          <button
            className="mt-5 rounded-xl border border-[#d8d1ca] bg-white px-4 py-2.5 font-semibold text-[#111111] transition hover:bg-[#f7f5f2]"
            onClick={onLogout}
            type="button"
          >
            Logout
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default StaffDashboardPage;
